import React, { useState, useEffect, useRef } from "react";
import Header from "../../../../Componets/Header";
import Loadercomponent from "../../../../Componets/Loader";
import {
  Card,
  Table,
  DatePicker,
  Skeleton,
  Alert,
  Row,
  Col,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { getLedgerEntries } from "../../../../Controllers/Accounting/Services/generalLedgerService";
import headers from "../../../../Data/Tables/Accounting/ReportEntry/BalanceSheet/headers";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const { Title, Text } = Typography;

const BalanceSheetReport = () => {
  const [loader, setLoader] = useState(false);
  const [header, setHeader] = useState(headers);
  const [dateRange, setDateRange] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [balanceSheetData, setBalanceSheetData] = useState({
    assets: [],
    liabilities: [],
    equity: [],
    income: [],
    expenses: [],
  });

  const reportRef = useRef();

  const downloadPDF = () => {
    const pdf = new jsPDF("p", "mm", "a4");

    // Title
    pdf.setFontSize(18);
    pdf.text("Balance Sheet Report", 14, 20);

    // Period
    if (dateRange.start && dateRange.end) {
      pdf.setFontSize(11);
      pdf.text(
        `Period: ${dayjs(dateRange.start).format("MMM D, YYYY")} - ${dayjs(
          dateRange.end
        ).format("MMM D, YYYY")}`,
        14,
        30
      );
    }

    let currentY = 40;

    // Helper to render category sections
    const renderCategoryPDF = (title, data) => {
      if (!data || data.length === 0) return;

      pdf.setFontSize(14);
      pdf.text(title, 14, currentY);
      currentY += 6;

      data.forEach((typeData) => {
        pdf.setFontSize(12);
        pdf.text(
          `Total: ${typeData.totalBalance.toFixed(2)}`,
          14,
          currentY
        );
        currentY += 6;

        autoTable(pdf, {
          startY: currentY,
          head: [["Code", "Account Name", "Balance"]],
          body: typeData.accounts.map((acc) => [
            acc.code,
            acc.name,
            acc.balance.toFixed(2),
          ]),
          theme: "grid",
          styles: { fontSize: 10 },
          headStyles: { fillColor: [24, 144, 255] },
        });

        currentY = pdf.lastAutoTable.finalY + 10;
      });

      pdf.setFontSize(12);
      const totalBalance = data.reduce(
        (sum, type) => sum + type.totalBalance,
        0
      );
      pdf.text(`Total ${title}: ${totalBalance.toFixed(2)}`, 14, currentY);
      currentY += 12;
    };

    renderCategoryPDF("Assets", balanceSheetData.assets);
    renderCategoryPDF("Liabilities", balanceSheetData.liabilities);
    renderCategoryPDF("Equity", balanceSheetData.equity);
    renderCategoryPDF("Income", balanceSheetData.income);
    renderCategoryPDF("Expenses", balanceSheetData.expenses);

    pdf.setFontSize(14);
    pdf.text("Balance Sheet Summary", 14, currentY);
    currentY += 8;

    pdf.setFontSize(12);
    pdf.text(`Total Assets: ${totals.totalAssets.toFixed(2)}`, 14, currentY);
    currentY += 6;
    pdf.text(
      `Total Liabilities: ${totals.totalLiabilities.toFixed(2)}`,
      14,
      currentY
    );
    currentY += 6;
    pdf.text(`Total Equity: ${totals.totalEquity.toFixed(2)}`, 14, currentY);
    currentY += 6;
    pdf.text(
      `Liabilities + Equity: ${(
        totals.totalLiabilities + totals.totalEquity
      ).toFixed(2)}`,
      14,
      currentY
    );
    currentY += 6;

    if (isBalanced) {
      pdf.setTextColor(0, 128, 0);
      pdf.text("✔ Balance Sheet Balanced", 14, currentY);
    } else {
      pdf.setTextColor(200, 0, 0);
      pdf.text("✘ Balance Sheet Imbalance", 14, currentY);
    }

    pdf.save("BalanceSheetReport.pdf");
  };

  const formdata = (data) => {
    console.log("Form data received:", data);
    setDateRange(data.date);
  };

  const fetchTransactions = async () => {
    setLoader(true);
    setTransactions([]); 
    try {
      const requestData = {
        source: "Profit-Loss",
        sort: "date",
        ...(dateRange.start &&
          dateRange.end && {
            date: { start: dateRange.start, end: dateRange.end },
          }),
      };

      const response = await getLedgerEntries(requestData);
      console.log("Ledger Response:", response);

      if (response.success) {
        setTransactions(response.data.transactions || []);
        processBalanceSheetData(response.data.transactions || []);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
    setLoader(false);
  };

  const processBalanceSheetData = (transactions) => {
    const categorizedData = {
      assets: { accounts: {}, totalBalance: 0 },
      liabilities: { accounts: {}, totalBalance: 0 },
      equity: { accounts: {}, totalBalance: 0 },
      income: { accounts: {}, totalBalance: 0 },
      expenses: { accounts: {}, totalBalance: 0 },
    };

    transactions.forEach((transaction) => {
      // Use the account.category directly from the transaction data
      const category = transaction.account?.category;

      // Only process if category is one of the 5 basic categories
      if (!category || !categorizedData.hasOwnProperty(category.toLowerCase())) {
        console.warn(`Unknown or missing category: ${category} for account`, transaction.account);
        return;
      }

      const key = category.toLowerCase();
      const accountCode = transaction.account_code;
      const accountName = transaction.account_name;

      if (!categorizedData[key].accounts[accountCode]) {
        categorizedData[key].accounts[accountCode] = {
          code: accountCode,
          name: accountName,
          balance: 0,
          transactions: [],
        };
      }

      const account = categorizedData[key].accounts[accountCode];

      let balanceChange = 0;
      
      if (category === "Assets" || category === "Expenses") {
        balanceChange = transaction.debit - transaction.credit;
      } else {
        balanceChange = transaction.credit - transaction.debit;
      }

      account.balance += balanceChange;
      account.transactions.push(transaction);
      categorizedData[key].totalBalance += balanceChange;
    });

    const formattedData = {
      assets: formatCategoryData(categorizedData.assets),
      liabilities: formatCategoryData(categorizedData.liabilities),
      equity: formatCategoryData(categorizedData.equity),
      income: formatCategoryData(categorizedData.income),
      expenses: formatCategoryData(categorizedData.expenses),
    };

    console.log("Formatted Balance Sheet Data:", formattedData);
    setBalanceSheetData(formattedData);
  };

  const formatCategoryData = (categoryData) => {
    const accounts = Object.values(categoryData.accounts);
    
    if (accounts.length === 0) {
      return [];
    }

    return [
      {
        accounts: accounts.map((account) => ({
          ...account,
          key: account.code,
        })),
        totalBalance: categoryData.totalBalance,
      },
    ];
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [dateRange]);

  const renderCategorySection = (title, data, category) => {
    if (!data || data.length === 0) return null;

    const totalBalance = data[0]?.totalBalance || 0;

    return (
      <Card title={title} style={{ marginBottom: 24 }}>
        <Table
          columns={[
            {
              title: "Account Code",
              dataIndex: "code",
              key: "code",
              width: 120,
            },
            { title: "Account Name", dataIndex: "name", key: "name" },
            {
              title: "Balance",
              dataIndex: "balance",
              key: "balance",
              render: (value) => (
                <Text
                  strong
                  style={{ color: value >= 0 ? "#52c41a" : "#ff4d4f" }}
                >
                  {value.toFixed(2)}
                </Text>
              ),
              align: "right",
            },
          ]}
          dataSource={data[0]?.accounts || []}
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
        />

        <div
          style={{
            padding: "12px",
            backgroundColor: "#e6f7ff",
            borderTop: "2px solid #91d5ff",
            textAlign: "right",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          Total {title}: {totalBalance.toFixed(2)}
        </div>
      </Card>
    );
  };

  const calculateTotals = () => {
    const totalAssets = balanceSheetData.assets.reduce(
      (sum, type) => sum + type.totalBalance,
      0
    );
    const totalLiabilities = balanceSheetData.liabilities.reduce(
      (sum, type) => sum + type.totalBalance,
      0
    );
    const totalEquity = balanceSheetData.equity.reduce(
      (sum, type) => sum + type.totalBalance,
      0
    );
    const netIncome =
      balanceSheetData.income.reduce(
        (sum, type) => sum + type.totalBalance,
        0
      ) -
      balanceSheetData.expenses.reduce(
        (sum, type) => sum + type.totalBalance,
        0
      );

    return {
      totalAssets,
      totalLiabilities,
      totalEquity: totalEquity + netIncome,
      netIncome,
    };
  };

  const totals = calculateTotals();
  const isBalanced =
    Math.abs(
      totals.totalAssets - (totals.totalLiabilities + totals.totalEquity)
    ) < 0.01;

  const headerReset = () => {
    let componentData = [];
    headers?.components?.map((value, index) => {
      componentData?.push({
        ...value,
        value: null,
      });
    });
    setHeader({ ...headers, components: componentData });
  };

  useEffect(() => {
    headerReset();
  }, []);

  console.log("Balance Sheet Data:", balanceSheetData);
  console.log("Totals:", totals);

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <div className="flex gap-3 mb-3 justify-end">
        <button
          onClick={downloadPDF}
          className="px-4 py-2 rounded border border-gray-500 text-gray-600 bg-transparent  hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-100"
        >
          Export as PDF
        </button>
      </div>

      <Loadercomponent loader={loader}>
        <div ref={reportRef} style={{ padding: 24 }}>
          <Card>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  Balance Sheet Report
                </Title>
              </Col>
              <Col>
                <Text type="secondary">
                  {dateRange.start && dateRange.end
                    ? `Period: ${dayjs(dateRange.start).format(
                        "MMM D, YYYY"
                      )} - ${dayjs(dateRange.end).format("MMM D, YYYY")}`
                    : ""}
                </Text>
              </Col>
            </Row>
          </Card>

          <Skeleton loading={loader} active>
            {renderCategorySection("Assets", balanceSheetData.assets, "Assets")}

            {renderCategorySection(
              "Liabilities",
              balanceSheetData.liabilities,
              "Liabilities"
            )}

            {renderCategorySection("Equity", balanceSheetData.equity, "Equity")}

            {(balanceSheetData.income.length > 0 ||
              balanceSheetData.expenses.length > 0) && (
              <Card title="Income & Expenses" style={{ marginBottom: 24 }}>
                {balanceSheetData.income.length > 0 &&
                  renderCategorySection(
                    "Income",
                    balanceSheetData.income,
                    "Income"
                  )}
                {balanceSheetData.expenses.length > 0 &&
                  renderCategorySection(
                    "Expenses",
                    balanceSheetData.expenses,
                    "Expenses"
                  )}

                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#f6ffed",
                    borderTop: "2px solid #b7eb8f",
                    textAlign: "right",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  Net Income: {totals.netIncome.toFixed(2)}
                </div>
              </Card>
            )}

            <Card title="Balance Sheet Summary" style={{ marginTop: 24 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div
                    style={{
                      padding: "16px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                    }}
                  >
                    <Title level={4}>Assets</Title>
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#52c41a",
                      }}
                    >
                      {totals.totalAssets.toFixed(2)}
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div
                    style={{
                      padding: "16px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                    }}
                  >
                    <Title level={4}>Liabilities & Equity</Title>
                    <div style={{ marginBottom: "8px" }}>
                      <span>Liabilities: </span>
                      <span style={{ float: "right" }}>
                        {totals.totalLiabilities.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <span>Equity: </span>
                      <span style={{ float: "right" }}>
                        {totals.totalEquity.toFixed(2)}
                      </span>
                    </div>
                    <hr style={{ margin: "8px 0" }} />
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#52c41a",
                      }}
                    >
                      {(totals.totalLiabilities + totals.totalEquity).toFixed(
                        2
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {!isBalanced && (
                <Alert
                  message="Balance Sheet Imbalance"
                  description={`Total Assets (${totals.totalAssets.toFixed(
                    2
                  )}) do not equal Total Liabilities + Equity (${(
                    totals.totalLiabilities + totals.totalEquity
                  ).toFixed(2)})`}
                  type="error"
                  style={{ marginTop: 16 }}
                />
              )}

              {isBalanced && (
                <Alert
                  message="Balance Sheet Balanced"
                  description="Total Assets equal Total Liabilities + Equity"
                  type="success"
                  style={{ marginTop: 16 }}
                />
              )}
            </Card>
          </Skeleton>
        </div>
      </Loadercomponent>
    </Header>
  );
};

export default BalanceSheetReport;